// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import {
  DEFAULT_PLAYER_COLOR,
  GUEST_PLAYER_COLOR,
  DEFAULT_PLAYER_EMOJI,
  GUEST_PLAYER_EMOJI,
} from '../../constants';
import { formatPlayerName } from '../../utils/helpers';
import type { PlayerInfo } from '../../types/game';

interface PlayerListItemProps {
  player: PlayerInfo;
  isCurrentUser: boolean;
  isFirstPlayer: boolean;
  isHost: boolean;
  copiedClaimCode: string | null;
  onCopyClaimCode: (code: string) => void;
}

export function PlayerListItem({
  player,
  isCurrentUser,
  isFirstPlayer,
  isHost,
  copiedClaimCode,
  onCopyClaimCode,
}: PlayerListItemProps) {
  return (
    <div
      className="flex items-center justify-between bg-white/5 rounded-lg p-3"
      data-testid={`lobby-player-item-${player.player_id}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center font-bold text-white text-2xl relative"
          style={{
            backgroundColor: player.is_guest
              ? GUEST_PLAYER_COLOR
              : player.theme_primary_color || DEFAULT_PLAYER_COLOR,
          }}
        >
          {player.profile_emoji || (player.is_guest ? GUEST_PLAYER_EMOJI : DEFAULT_PLAYER_EMOJI)}
          {player.is_npc && (
            <span
              className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs px-1 rounded"
              title="AI Player"
            >
              AI
            </span>
          )}
        </div>
        <div>
          <p className="font-medium" data-testid={`lobby-player-name-${player.player_id}`}>
            {formatPlayerName(player.display_name, isCurrentUser)}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {isFirstPlayer && (
              <span
                className="text-xs text-yellow-400"
                data-testid={`lobby-player-host-badge-${player.player_id}`}
              >
                Host
              </span>
            )}
            {player.is_npc && (
              <span
                className="text-xs text-purple-400"
                data-testid={`lobby-player-npc-badge-${player.player_id}`}
              >
                AI Player
              </span>
            )}
            {/* Show claim code to host only */}
            {isHost && player.is_npc && player.claim_code && (
              <button
                onClick={() => onCopyClaimCode(player.claim_code!)}
                className="text-xs font-mono bg-purple-900/50 px-2 py-0.5 rounded hover:bg-purple-800/50 transition-colors"
                title="Click to copy claim code"
                data-testid={`lobby-claim-code-${player.player_id}`}
              >
                {copiedClaimCode === player.claim_code ? '✓ Copied!' : player.claim_code}
              </button>
            )}
          </div>
        </div>
      </div>
      <div
        className={`h-2 w-2 rounded-full ${player.is_connected ? 'bg-green-500' : 'bg-gray-500'}`}
        data-testid={`lobby-player-status-${player.player_id}`}
      />
    </div>
  );
}
